import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ISmallRole } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapRole from '@src/APIs/graphQl/Role/changeMapRole';
import createAndMapRole from '@src/APIs/graphQl/Role/createAndMapRole';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { setEmployeeRoles, setPrimaryRoleId } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { Building, Eye, Plus, Search, Star, Trash2, Users } from 'lucide-react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ProfileTabHeader } from './ProfileTabHeader';

export function RolesTab(): JSX.Element {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const showErrorDialog = useErrorDialog();

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).profileSettings;
    const allRoles = useSelector((state: AppState) => state.company.allRoles);
    const employee = useSelector((state: AppState) => state.employee);
    const offline = useSelector((state: AppState) => state.client.offline);
    const isAdmin = useSelector((state: AppState) => state.permissions.admin);

    const [selectedRole, setSelectedRole] = React.useState<ISmallRole | null>(null);
    const [newRoleName, setNewRoleName] = React.useState('');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [deletingIds, setDeletingIds] = React.useState<number[]>([]);
    const [error, setError] = React.useState<string | null>(null);

    const primaryRoleId = useSelector((state: AppState) => state.employee.primaryRoleId);

    const handleAddExisting = async () => {
        if (!selectedRole) {
            setError('Bitte wählen Sie eine Rolle aus');
            return;
        }

        setError(null);
        setSelectedRole(null);

        try {
            await changeMapRole(selectedRole.id, true);
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Hinzufügen der Rolle');
        }
    };

    const handleCreateNew = async () => {
        if (!newRoleName.trim()) {
            setError('Bitte geben Sie einen Namen ein');
            return;
        }

        setError(null);
        const roleName = newRoleName;
        setNewRoleName('');

        try {
            await createAndMapRole(roleName);
        } catch (error) {
            showErrorDialog(error as Error, 'Fehler beim Erstellen der Rolle');
        }
    };

    const handleRemove = async (role: { title: string; id?: number }) => {
        if (!role.id) return;

        setDeletingIds((prev) => [...prev, role.id!]);

        try {
            await changeMapRole(role.id, false);
            setDeletingIds((prev) => prev.filter((d) => d !== role.id));
            dispatch(setEmployeeRoles(employee.roles.filter((r) => r.id !== role.id)));
        } catch (error) {
            setDeletingIds((prev) => prev.filter((d) => d !== role.id));
            showErrorDialog(error as Error, 'Fehler beim Entfernen der Rolle');
        }
    };

    if (!employee) return <LoadingBox />;

    const roles = [...employee.roles].sort((a, b) => a.title.localeCompare(b.title));
    const filteredRoles = roles.filter((role) => role.title.toLowerCase().includes(searchTerm.toLowerCase()));

    // Initialize primary role to first role if not set
    React.useEffect(() => {
        console.log('🔍 RolesTab Debug:', {
            rolesCount: roles.length,
            primaryRoleId,
            firstRoleId: roles[0]?.id,
        });
        if (roles.length > 0 && primaryRoleId === null) {
            console.log('🚀 Auto-setting first role as primary:', roles[0].id);
            dispatch(setPrimaryRoleId(roles[0].id || null));
        }
    }, [roles, primaryRoleId, dispatch]);

    const handleSetPrimaryRole = (roleId: number | undefined) => {
        if (roleId) {
            console.log('🔄 Setting Primary Role ID:', roleId);
            dispatch(setPrimaryRoleId(roleId));
            console.log('✅ Dispatched setPrimaryRoleId');
            /* TODO: Backend Implementation for Primary Role Feature
             *
             * REQUIRED CHANGES:
             *
             * 1. DATABASE SCHEMA:
             *    - Add 'isPrimary' boolean field to employee_roles table
             *    - Ensure only one role per employee can be primary (unique constraint)
             *
             * 2. INTERFACE UPDATES:
             *    - Update IEmployeeRoleReference in IEmployee.ts:
             *      interface IEmployeeRoleReference extends IReference {
             *        isPrimary?: boolean;
             *      }
             *
             * 3. GRAPHQL SCHEMA:
             *    - Add setPrimaryRole mutation:
             *      setPrimaryRole(employeeId: ID!, roleId: ID!): Boolean
             *    - Update employee query to include isPrimary field
             *
             * 4. API IMPLEMENTATION:
             *    - Create setPrimaryRole function in Role API
             *    - Ensure atomic operation (unset old primary, set new primary)
             *    - Update Redux state after successful API call
             *
             * 5. PROFILE HEADER UPDATE:
             *    - Modify ProfileHeader.tsx to use primary role instead of roles[0]
             *    - Show primary role chip with special styling
             *
             * 6. PERSISTENCE:
             *    - Replace local state with API call:
             *      await setPrimaryRoleAPI(roleId);
             *    - Handle error cases and rollback on failure
             *
             * IMPACT: This feature enables users to control which role appears
             * in their profile header, improving professional representation.
             */
        }
    };
    const availableRoles = allRoles.filter(
        (role) => !roles.find((existing) => existing.id === role.id || existing.title === role.title),
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ProfileTabHeader title={langStrings.yourRoles} subtitle="Verwalte deine Rollen und Verantwortlichkeiten" />

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Rolle hinzufügen
                    </Typography>

                    {/* Bestehende Rolle hinzufügen */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 2,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            bgcolor: alpha(theme.palette.info.main, 0.02),
                            borderRadius: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Search size={16} color={theme.palette.info.main} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                Bestehende Rolle hinzufügen
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Durchsucht vorhandene Rollen in deinem Unternehmen
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Autocomplete
                                value={selectedRole}
                                onChange={(_, newValue) => setSelectedRole(newValue)}
                                options={availableRoles}
                                getOptionLabel={(option) => option.title}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Rolle auswählen..."
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <Users size={16} style={{ opacity: 0.6 }} />
                                        <Typography>{option.title}</Typography>
                                    </Box>
                                )}
                                sx={{ flex: 1 }}
                                disabled={offline}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleAddExisting}
                                disabled={!selectedRole || offline}
                                startIcon={<Plus size={16} />}
                            >
                                Hinzufügen
                            </Button>
                        </Box>
                    </Paper>

                    {/* Neue Rolle erstellen - nur für Admins */}
                    {isAdmin && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                bgcolor: alpha(theme.palette.success.main, 0.02),
                                borderRadius: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Plus size={16} color={theme.palette.success.main} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: theme.palette.success.main }}
                                >
                                    Neue Rolle erstellen
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Erstellt eine komplett neue Rolle für dein Unternehmen
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                    placeholder="Name der neuen Rolle..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ flex: 1 }}
                                    disabled={offline}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleCreateNew();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleCreateNew}
                                    disabled={!newRoleName.trim() || offline}
                                    startIcon={<Plus size={16} />}
                                >
                                    Erstellen
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>

                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, mt: 0 }}>
                        {error}
                    </Alert>
                )}

                {/* Suchfeld für bestehende Rollen */}
                <Box sx={{ px: { xs: 1, md: 3, lg: 4 }, py: { xs: 1, md: 3, lg: 4 }, pt: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Deine Rollen ({roles.length})
                    </Typography>
                    <TextField
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="In deinen Rollen suchen..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <Search size={20} style={{ marginRight: 8, opacity: 0.6 }} />,
                        }}
                        sx={{ maxWidth: 400 }}
                    />
                </Box>

                <TableContainer sx={{ px: { xs: 1, md: 3, lg: 4 }, pb: { xs: 1, md: 3, lg: 4 } }}>
                    <Table sx={{ '& .MuiTableCell-root': { px: { xs: 1, md: 3, lg: 4 } } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 140 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Building size={14} />
                                        Bereich
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, width: 100 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Star size={14} />
                                        Fokus
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>
                                    Aktionen
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRoles.map((role) => (
                                <TableRow
                                    key={role.id || role.title}
                                    sx={{
                                        borderLeft: '2px solid transparent',
                                        '&:hover': {
                                            backgroundColor: `${alpha(theme.palette.primary.main, 0.02)} !important`,
                                            borderLeft: `2px solid ${theme.palette.primary.main}`,
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Users size={16} style={{ opacity: 0.6 }} />
                                            <Box>
                                                <Typography>{role.title}</Typography>
                                                {!role.id && (
                                                    <Chip
                                                        label="Neu"
                                                        size="small"
                                                        color="primary"
                                                        sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {/* TODO: Implement OU/Organization support for roles
                                            - Add orgUnit field to ISmallRole interface
                                            - Display actual organization/department name
                                            - Support for multi-company/matrix organization scenarios
                                            - Enable role assignment to specific organizational units */}
                                        <Typography variant="body2" sx={{ opacity: 0.6, fontStyle: 'italic' }}>
                                            Unternehmensweite Rolle
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {/* TODO: Implement primary role functionality
                                            - Add isPrimary field to IEmployeeRoleReference
                                            - Allow setting one role as primary/focus role
                                            - Display primary role in profile header
                                            - API endpoint to set/unset primary role */}
                                        <IconButton
                                            size="small"
                                            onClick={() => handleSetPrimaryRole(role.id)}
                                            disabled={offline}
                                            sx={{
                                                color:
                                                    primaryRoleId === role.id
                                                        ? theme.palette.warning.main
                                                        : theme.palette.action.disabled,
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                },
                                            }}
                                        >
                                            <Star
                                                size={16}
                                                fill={primaryRoleId === role.id ? 'currentColor' : 'none'}
                                            />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            {role.id && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/roles/${role.id}`)}
                                                    sx={{
                                                        color: theme.palette.info.main,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <Eye size={16} />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemove(role)}
                                                disabled={deletingIds.includes(role.id as number) || offline}
                                                sx={{
                                                    color: theme.palette.error.main,
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                                    },
                                                }}
                                            >
                                                <Trash2 size={16} />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredRoles.length === 0 && roles.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                            Keine Rollen gefunden für &quot;{searchTerm}&quot;
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {roles.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 2,
                                                opacity: 0.7,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 64,
                                                    height: 64,
                                                    borderRadius: '50%',
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mb: 1,
                                                }}
                                            >
                                                <Users size={32} style={{ opacity: 0.6 }} />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                Noch keine Rollen
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ opacity: 0.7, mb: 2, textAlign: 'center', maxWidth: 300 }}
                                            >
                                                Fügen Sie Ihre erste Rolle hinzu, um Ihre Verantwortlichkeiten zu
                                                verwalten
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                startIcon={<Plus size={16} />}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    px: 3,
                                                    py: 1.5,
                                                    '&:hover': {
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                    },
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                Rolle hinzufügen
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
